import { useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { History } from "@nens/rana/api-v1";

import { useRanaSearchState } from "@/hooks/router/rana-search-state";
import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";
import { useFileHistoryQuery } from "@/queries/query-file-history";
import { getPrettyTime } from "@/utils/get-pretty-time";

import { revertFileToVersionAction } from "../components/project-file-confirm-revert-version-modal";

type ProjectFileHistroyModalType = { type: "revert-to-version"; item: History } | null;

export const useProjectFileHistory = ({ path, fileName }: { path: string; fileName?: string }) => {
  const intl = useIntl();
  const { canWriteProject } = useProjectActionAllowed();
  const fileId = path + (fileName ?? "");

  const filesHistoryQuery = useFileHistoryQuery(fileId);
  const [searchString, setSearchString] = useRanaSearchState("histSearch", "");
  const fileHistories = useMemo(() => {
    const matchesSearch = (string: string) =>
      string.toLowerCase().includes(searchString.toLowerCase());

    return (
      filesHistoryQuery.data?.items.filter(
        (item) =>
          // Message
          matchesSearch(item.message) ||
          // Commited by
          matchesSearch(`${item.committedBy.givenName} ${item.committedBy.familyName}`) ||
          // Created at
          matchesSearch(getPrettyTime(item.createdAt))
      ) ?? []
    );
  }, [filesHistoryQuery.data, searchString]);
  const [openModal, setOpenModal] = useState<ProjectFileHistroyModalType>(null);
  const closeModal = () => setOpenModal(null);
  const getActions = (historyItem: History, isFirst: boolean) => {
    if (isFirst || !canWriteProject || !fileName) return [];
    return [
      revertFileToVersionAction({
        action: () => {
          setOpenModal({ type: "revert-to-version", item: historyItem });
        },
        intl,
      }),
    ];
  };

  return {
    fileId,
    openModal,
    closeModal,
    getActions,
    fileHistories,
    isLoading: filesHistoryQuery.isLoading,
    searchString,
    setSearchString,
  };
};
